import type { ChangeEvent, KeyboardEvent as ReactKeyboardEvent } from 'react';
import {
  FieldValues,
  useController,
  UseControllerProps,
} from 'react-hook-form';
import { TextField, TextFieldProps } from '@stitch-fix/mode-react';
import { G, S } from '@mobily/ts-belt';

type InteritedTextFieldProps = Pick<
  TextFieldProps,
  | 'autoFocus'
  | 'helpText'
  | 'hideLabel'
  | 'inputMode'
  | 'label'
  | 'maxLength'
  | 'mb'
  | 'placeholder'
  | 'rows'
  | 'state'
>;

type ControlledTextFieldChangeEvent =
  | ChangeEvent<HTMLInputElement>
  | ChangeEvent<HTMLTextAreaElement>;

type ControlledTextFieldKeyboardEvent =
  | ReactKeyboardEvent<HTMLInputElement>
  | ReactKeyboardEvent<HTMLTextAreaElement>;

type BaseControlledTextFieldProps<TFieldValues extends FieldValues> = {
  control: UseControllerProps<TFieldValues>['control'];
  name: UseControllerProps<TFieldValues>['name'];

  /**
   * autocomplete sources:
   *
   * - https://www.chromium.org/developers/design-documents/create-amazing-password-forms/
   * - https://html.spec.whatwg.org/multipage/form-control-infrastructure.html#autofilling-form-controls%3A-the-autocomplete-attribute
   * - https://www.chromium.org/developers/design-documents/form-styles-that-chromium-understands/
   */
  autocomplete?: HTMLInputElement['autocomplete'];
  disabled?: HTMLInputElement['disabled'];
  id?: HTMLInputElement['id'];
  max?: HTMLInputElement['max'];
  min?: HTMLInputElement['min'];
  type?: HTMLInputElement['type'];
  /**
   * Optional value formatter fired before the internal react-hook-form onChange
   * receives the next value.
   */
  formatValue?: (
    value: string,
    previousValue: string,
    event: ControlledTextFieldChangeEvent,
  ) => string;
  /**
   * Optional side-effect callback fired after the internal react-hook-form
   * onChange updates the form state.
   */
  onValueChange?: (
    value: string,
    event?: ControlledTextFieldChangeEvent,
  ) => void;
  onKeyDown?: (event: ControlledTextFieldKeyboardEvent) => void;
} & InteritedTextFieldProps;

// Allows undefined label for hidden field type
interface HiddenControlledTextFieldProps<TFieldValues extends FieldValues>
  extends Omit<BaseControlledTextFieldProps<TFieldValues>, 'label' | 'type'> {
  label?: TextFieldProps['label'];
  type: 'hidden';
}

type ControlledTextFieldProps<TFieldValues extends FieldValues> =
  | BaseControlledTextFieldProps<TFieldValues>
  | HiddenControlledTextFieldProps<TFieldValues>;

/**
 * This component allows [React Hook Form](https://react-hook-form.com/)
 * to easily communicate with Mode React's [TextField](https://mode-react.daylight.stitchfix.com/?path=/docs/components-text-field--example)
 */
export const ControlledTextField = <TFieldValues extends FieldValues>({
  autocomplete,
  control,
  label,
  name,
  max,
  min,
  helpText,
  disabled,
  state,
  mb = 1,
  formatValue,
  onValueChange,
  onKeyDown,
  ...rest
}: ControlledTextFieldProps<TFieldValues>) => {
  const {
    field: { name: textFieldName, onChange, ref, value },
    fieldState: { error },
  } = useController({ control, name });

  const textFieldValue =
    value || typeof value === 'number' ? String(value) : '';

  const handleChange = (
    newValue: string,
    event: ControlledTextFieldChangeEvent,
  ) => {
    const nextValue = formatValue
      ? formatValue(newValue, textFieldValue, event)
      : newValue;

    onChange(nextValue);
    onValueChange?.(nextValue, event);
  };
  const hasError =
    G.isObject(error) &&
    G.isString(error.message) &&
    S.isNotEmpty(error.message);
  const getState = () => {
    if (state) return state;
    if (hasError) return 'error';
    if (disabled) return 'disabled';

    return 'default';
  };

  return (
    <TextField
      autoComplete={autocomplete}
      helpText={hasError && (helpText || error.message)}
      label={label}
      max={max}
      mb={mb}
      min={min}
      name={textFieldName}
      onChange={handleChange}
      onKeyDown={onKeyDown}
      state={getState()}
      value={textFieldValue}
      ref={ref}
      {...rest}
    />
  );
};
